import React from 'react';
import { cn } from '@/utils/cn';

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    { checked, defaultChecked, onCheckedChange, disabled, className, size = 'md', ...props },
    ref
  ) => {
    const [_, setInternalChecked] = React.useState(defaultChecked || false);

    const isControlled = checked !== undefined;
    const checkedValue = isControlled ? checked : internalChecked;

    const handleClick = () => {
      if (disabled) return;

      const newChecked = !checkedValue;

      if (!isControlled) {
        setInternalChecked(newChecked);
      }

      onCheckedChange?.(newChecked);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        handleClick();
      }
    };

    const sizeClasses = {
      sm: {
        root: 'h-4 w-7',
        thumb: 'h-3 w-3 data-[state=checked]:translate-x-3',
      },
      md: {
        root: 'h-5 w-9',
        thumb: 'h-4 w-4 data-[state=checked]:translate-x-4',
      },
      lg: {
        root: 'h-6 w-11',
        thumb: 'h-5 w-5 data-[state=checked]:translate-x-5',
      },
    };

    return (
      <button
        ref={ref}
        role="switch"
        type="button"
        aria-checked={checkedValue}
        data-state={checkedValue ? 'checked' : 'unchecked'}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
          'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
          'focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50',
          checkedValue ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-200 hover:bg-gray-300',
          disabled && (checkedValue ? 'hover:bg-red-600' : 'hover:bg-gray-200'),
          sizeClasses[size].root,
          className
        )}
        {...props}
      >
        <span
          data-state={checkedValue ? 'checked' : 'unchecked'}
          className={cn(
            'pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform',
            checkedValue ? sizeClasses[size].thumb : 'translate-x-0',
            sizeClasses[size].thumb.split(' ').slice(0, 2).join(' ') // Get only h- and w- classes
          )}
        />
      </button>
    );
  }
);

Switch.displayName = 'Switch';

export type { SwitchProps };
